import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { faHeart } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardRepliesItem, DashboardRepliesSection } from "../schema/dashboard";
import { formatRelativeTime, personRowFields } from "../utils/personRowFields";
import { DashboardBox } from "./DashboardBox";

interface ReEngageBoxProps {
	section: DashboardRepliesSection;
}

const formatTotal = (total: number) => (total >= 1000 ? "1000+" : String(total));

// Re-engage rows lead to the Ready-to-send ("ready to review") screen, filtered to the
// prospect's smart list via the JSON `listIds` param (see ReadyToSend.getSelectedListsFromUrl).
const reviewListUrl = (listId: number, listName: string): string => {
	const listIds = JSON.stringify([{ id: listId, title: listName }]);

	return `${ROUTE_CONFIG.MESSAGE_CENTER}?listIds=${encodeURIComponent(listIds)}`;
};

const ReEngageRow: FunctionComponent<{ row: DashboardRepliesItem }> = ({ row }) => {
	const fields = personRowFields(row);

	return (
		<Link
			className="group flex items-center gap-3 px-4 py-3 hover:bg-background-light"
			to={reviewListUrl(fields.listId, fields.listName)}
		>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-text truncate">
					{fields.displayName}
					{fields.organization && (
						<span className="text-text-meta font-normal"> · {fields.organization}</span>
					)}
				</div>
				<div className="text-xs text-text-meta truncate">{fields.listName}</div>
			</div>
			<span className="text-[11px] text-text-meta w-12 text-right shrink-0">
				{formatRelativeTime(fields.updatedAtIso)}
			</span>
		</Link>
	);
};

const CountPill: FunctionComponent<{ count: number }> = ({ count }) => (
	<span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[11px] font-semibold bg-status-warning-light text-text-warning mr-1">
		{formatTotal(count)}
	</span>
);

export const ReEngageBox: FunctionComponent<ReEngageBoxProps> = ({ section }) => {
	const { t } = useTranslation();
	const items = section.items;

	return (
		<DashboardBox
			accent="yellow"
			dismissible={false}
			footer={
				items.length > 0 ? (
					<Link to={ROUTE_CONFIG.MESSAGE_CENTER}>
						{t("dashboard.reengage.viewAll", { count: formatTotal(section.total) })}
					</Link>
				) : undefined
			}
			icon={<Icon className="w-4 h-4" icon={faHeart} />}
			subtitle={t("dashboard.reengage.subtitle")}
			title={
				<>
					<CountPill count={section.total} />
					{t("dashboard.reengage.title")}
				</>
			}
		>
			{items.length === 0 ? (
				<div className="px-4 py-6 text-center text-xs text-text-meta">
					{t("dashboard.reengage.empty")}
				</div>
			) : (
				items.map((row) => <ReEngageRow key={row.id} row={row} />)
			)}
		</DashboardBox>
	);
};
